import type { Subtask, Task, TaskStatus, UserProfile } from './types'

export interface CampaignReviewTransition {
  status: 'Em análise'
  recipients: string[]
}

export interface CampaignReviewReopenTransition {
  status: 'Em execução'
}

export interface CampaignReviewApproval {
  approvedSubtaskIds: string[]
  status: 'Em análise' | 'Aprovada'
  subtasks: Subtask[]
}

export interface CampaignReviewApprovalWithdrawal {
  status: 'Em análise'
  subtasks: Subtask[]
}

export function campaignReviewers(task: Task) {
  if (!task.subtasks.length) return [task.owner]
  return [...new Set(task.subtasks.map(subtask => subtask.reviewer ?? task.owner))]
}

export function canReviewCampaign(task: Task, user: string) {
  return task.owner === user || task.subtasks.some(subtask => subtask.reviewer === user)
}

export function campaignIsReadyForReview(task: Task) {
  return task.subtasks.length > 0 && task.subtasks.every(subtask => subtask.status === 'Aprovada')
}

export function canBypassCampaignReviewRequirement(profile: UserProfile) {
  return profile === 'Administrador' || profile === 'Gerente' || profile === 'Coordenador'
}

export function canWithdrawCampaignTaskApproval(profile: UserProfile) {
  return profile === 'Administrador' || profile === 'Gerente' || profile === 'Coordenador'
}

export function canMoveCampaignToReview(task: Task, profile: UserProfile) {
  return campaignIsReadyForReview(task) || canBypassCampaignReviewRequirement(profile)
}

export function campaignReviewReopenTransition(task: Task | undefined, reopenedSubtaskId: string, nextStatus: TaskStatus | undefined, profile: UserProfile): CampaignReviewReopenTransition | undefined {
  if (!task || task.status !== 'Em análise' || !nextStatus || nextStatus === 'Aprovada' || !canBypassCampaignReviewRequirement(profile)) return undefined
  const reopenedSubtask = task.subtasks.find(subtask => subtask.id === reopenedSubtaskId)
  return reopenedSubtask?.status === 'Aprovada' ? { status: 'Em execução' } : undefined
}

export function subtaskIsApprovedInAnalysis(subtask: Subtask) {
  return Boolean(subtask.analysisApprovedAt)
}

export function approveCampaignReviewSubtasks(task: Task, requestedSubtaskIds: string[], approvedBy: string, approvedOn = new Date()): CampaignReviewApproval | undefined {
  if (task.status !== 'Em análise') return undefined
  const requestedIds = new Set(requestedSubtaskIds)
  const approvedSubtaskIds = task.subtasks
    .filter(subtask => requestedIds.has(subtask.id) && subtask.status === 'Aprovada' && !subtaskIsApprovedInAnalysis(subtask))
    .map(subtask => subtask.id)
  if (!approvedSubtaskIds.length) return undefined

  const approvedIds = new Set(approvedSubtaskIds)
  const approvedAt = approvedOn.toISOString()
  const subtasks = task.subtasks.map(subtask => approvedIds.has(subtask.id)
    ? { ...subtask, analysisApprovedAt: approvedAt, analysisApprovedBy: approvedBy }
    : subtask)
  const allApproved = subtasks.length > 0 && subtasks.every(subtaskIsApprovedInAnalysis)
  return { approvedSubtaskIds, status: allApproved ? 'Aprovada' : 'Em análise', subtasks }
}

export function withdrawCampaignReviewSubtaskApproval(task: Task, subtaskId: string): CampaignReviewApprovalWithdrawal | undefined {
  if (task.status !== 'Em análise' && task.status !== 'Aprovada') return undefined
  const approvedSubtask = task.subtasks.find(subtask => subtask.id === subtaskId)
  if (!approvedSubtask || !subtaskIsApprovedInAnalysis(approvedSubtask)) return undefined

  return {
    status: 'Em análise',
    subtasks: task.subtasks.map(subtask => subtask.id === subtaskId
      ? { ...subtask, analysisApprovedAt: undefined, analysisApprovedBy: undefined }
      : subtask),
  }
}

export function campaignReviewTransition(task: Task | undefined, completedSubtaskId: string, nextStatus: TaskStatus | undefined): CampaignReviewTransition | undefined {
  if (!task || nextStatus !== 'Aprovada' || task.status === 'Em análise' || !task.subtasks.length) return undefined
  const completedSubtask = task.subtasks.find(subtask => subtask.id === completedSubtaskId)
  if (!completedSubtask || completedSubtask.status === 'Aprovada') return undefined
  const taskAfterCompletion = {
    ...task,
    subtasks: task.subtasks.map(subtask => subtask.id === completedSubtaskId ? { ...subtask, status: nextStatus } : subtask),
  }
  return campaignIsReadyForReview(taskAfterCompletion) ? { status: 'Em análise', recipients: campaignReviewers(task) } : undefined
}
